import katex from 'katex'
import { html } from './h.js'
import { t, getLanguage } from './i18n.js'
import { ENTITY, KIND, ROLE, CALL_STATE, toDevice } from '@pine/core'

/* ------------------------------------------------------------------ *
 * Time
 * ------------------------------------------------------------------ */

/*
 * Every time shown here is a stamp in the server's clock (pine-core's
 * clock.js), turned into this device's: the time on the status bar is the
 * one people read the chat against, right or wrong. On a phone whose clock is
 * right the two are the same and nothing moves.
 */
const local = (ts) => new Date(toDevice(ts))

export function clockTime (ts) {
  return local(ts).toLocaleTimeString(getLanguage(), { hour: '2-digit', minute: '2-digit' })
}

/** Compact stamp for the chat list: time today, weekday this week, else date. */
export function listTime (ts) {
  if (!ts) return ''
  const date = local(ts)
  const now = new Date()
  const sameDay = date.toDateString() === now.toDateString()
  if (sameDay) return clockTime(ts)

  const yesterday = new Date(now)
  yesterday.setDate(now.getDate() - 1)
  if (date.toDateString() === yesterday.toDateString()) return t('time.yesterday')

  const days = (now - date) / 86400000
  if (days < 7) return date.toLocaleDateString(getLanguage(), { weekday: 'short' })
  if (date.getFullYear() === now.getFullYear()) {
    return date.toLocaleDateString(getLanguage(), { day: 'numeric', month: 'short' })
  }
  return date.toLocaleDateString(getLanguage(), { day: 'numeric', month: 'short', year: '2-digit' })
}

export function dayLabel (ts) {
  const date = local(ts)
  const now = new Date()
  if (date.toDateString() === now.toDateString()) return t('time.today')
  const yesterday = new Date(now)
  yesterday.setDate(now.getDate() - 1)
  if (date.toDateString() === yesterday.toDateString()) return t('time.yesterdayFull')
  return date.toLocaleDateString(getLanguage(), {
    day: 'numeric',
    month: 'long',
    year: date.getFullYear() === now.getFullYear() ? undefined : 'numeric'
  })
}

export function sameDay (a, b) {
  return local(a).toDateString() === local(b).toDateString()
}

function durationText (seconds) {
  return duration(seconds)
}

export function duration (seconds) {
  const s = Math.max(0, Math.floor(seconds))
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const sec = s % 60
  const pad = (n) => String(n).padStart(2, '0')
  return h ? `${h}:${pad(m)}:${pad(sec)}` : `${m}:${pad(sec)}`
}

/* ------------------------------------------------------------------ *
 * Numbers and names
 * ------------------------------------------------------------------ */

export function fileSize (bytes) {
  if (!bytes) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)))
  const value = bytes / Math.pow(1024, i)
  return (i === 0 ? value : value.toFixed(value >= 10 ? 0 : 1)) + ' ' + units[i]
}

export function shortId (id) {
  if (!id) return ''
  return id.slice(0, 5) + '…' + id.slice(-4)
}

export function initials (name, fallbackId = '') {
  const clean = (name || '').trim()
  if (!clean) return (fallbackId || '?').slice(0, 2).toUpperCase()
  const parts = clean.split(/\s+/).slice(0, 2)
  return parts.map((w) => [...w][0]).join('').toUpperCase()
}

/**
 * Deterministic avatar colour from an account id, so the same person is the
 * same colour on every device without storing anything.
 */
const AVATAR_COLORS = [
  '#2a8459', '#2b6cb0', '#6b46c1', '#b7791f', '#c05621',
  '#0f766e', '#9d174d', '#4338ca', '#166534', '#7c2d12'
]

export function avatarColor (id = '') {
  let hash = 0
  for (let i = 0; i < id.length; i++) hash = (hash * 31 + id.charCodeAt(i)) >>> 0
  return AVATAR_COLORS[hash % AVATAR_COLORS.length]
}

export function roleLabel (role) {
  switch (role) {
    case ROLE.OWNER: return t('role.owner')
    case ROLE.ADMIN: return t('role.admin')
    case ROLE.MODERATOR: return t('role.moderator')
    case ROLE.RESTRICTED: return t('role.restricted')
    default: return t('role.member')
  }
}

/* ------------------------------------------------------------------ *
 * Message text rendering
 * ------------------------------------------------------------------ */

const URL_RE = /\b((?:https?:\/\/|www\.)[^\s<>()]+[^\s<>().,;:!?'"])/gi

/**
 * Turn a message into VDOM: applies stored entities (bold, code, mentions,
 * spoilers …) and auto-links anything that looks like a URL.
 *
 * Entity offsets are UTF-16 code units, which is what the DOM and JS string
 * indices use, so no conversion is needed between what the composer measured
 * and what we slice here.
 */
export function renderText (text, entities = [], { onMention = null, onLink = null } = {}) {
  if (!text) return null

  const marks = [...(entities || [])]
  for (const match of findMath(text)) marks.push(match)
  for (const match of text.matchAll(URL_RE)) {
    marks.push({ type: ENTITY.LINK, offset: match.index, length: match[0].length, value: match[0] })
  }
  if (!marks.length) return text

  // Longest-first so a link inside a bold run still wins its own span.
  marks.sort((a, b) => a.offset - b.offset || b.length - a.length)

  const out = []
  let cursor = 0
  for (const mark of marks) {
    if (mark.offset < cursor) continue
    if (mark.offset > cursor) out.push(text.slice(cursor, mark.offset))
    const slice = text.slice(mark.offset, mark.offset + mark.length)
    out.push(wrapEntity(mark, slice, { onMention, onLink }))
    cursor = mark.offset + mark.length
  }
  if (cursor < text.length) out.push(text.slice(cursor))
  return out
}

function wrapEntity (mark, slice, handlers) {
  switch (mark.type) {
    case MATH: return Math_(mark)
    // A formula inside bold or italic is still a formula; inside code it is
    // what was typed.
    case ENTITY.BOLD: return html`<b>${withMath(slice)}</b>`
    case ENTITY.ITALIC: return html`<i>${withMath(slice)}</i>`
    case ENTITY.UNDERLINE: return html`<u>${withMath(slice)}</u>`
    case ENTITY.STRIKE: return html`<s>${withMath(slice)}</s>`
    case ENTITY.CODE: return html`<code>${slice}</code>`
    case ENTITY.PRE: return html`<pre>${slice}</pre>`
    case ENTITY.QUOTE: return html`<blockquote>${withMath(slice)}</blockquote>`
    case ENTITY.SPOILER: return html`<${Spoiler} text=${slice} />`
    case ENTITY.MENTION:
      return html`<a href="#" onClick=${(e) => {
        e.preventDefault()
        if (handlers.onMention) handlers.onMention(mark.value || slice)
      }}>${slice}</a>`
    case ENTITY.LINK: {
      const href = normaliseUrl(mark.value || slice)
      return html`<a href=${href} target="_blank" rel="noopener noreferrer"
                     onClick=${(e) => { if (handlers.onLink) handlers.onLink(e, href) }}>${slice}</a>`
    }
    default: return slice
  }
}

/* ------------------------------------------------------------------ *
 * Formulas
 * ------------------------------------------------------------------ */

/*
 * LaTeX, the way a language model writes it and the way people paste it:
 * $$…$$ and \[…\] on a line of their own, $…$ and \(…\) inside a sentence.
 * Anya's answers were full of these and showed as backslashes and dollar
 * signs. The single-dollar form follows Pandoc's rule — no space just inside
 * either dollar, no digit just after the closing one — which is what keeps
 * "$5 and $10" a sentence about money.
 */
const MATH = 'math'
const MATH_RE = /\$\$([\s\S]+?)\$\$|\\\[([\s\S]+?)\\\]|\\\(([\s\S]+?)\\\)|(?<![\\$\w])\$(?![\s$])((?:\\.|[^$\\\n])+?)(?<![\s\\])\$(?![\w$])/g

export function findMath (text) {
  const out = []
  if (!text || (text.indexOf('$') < 0 && text.indexOf('\\') < 0)) return out
  for (const match of text.matchAll(MATH_RE)) {
    const tex = match[1] ?? match[2] ?? match[3] ?? match[4]
    if (!tex || !tex.trim()) continue
    out.push({
      type: MATH,
      offset: match.index,
      length: match[0].length,
      value: tex.trim(),
      display: match[1] !== undefined || match[2] !== undefined
    })
  }
  return out
}

/** A piece of plain text, with any formulas in it drawn. */
function withMath (text) {
  const found = findMath(text)
  if (!found.length) return text
  const out = []
  let cursor = 0
  for (const mark of found) {
    if (mark.offset > cursor) out.push(text.slice(cursor, mark.offset))
    out.push(Math_(mark))
    cursor = mark.offset + mark.length
  }
  if (cursor < text.length) out.push(text.slice(cursor))
  return out
}

const rendered = new Map()

/*
 * KaTeX turns the TeX into HTML and MathML of its own making: what goes into
 * the page is its output, never the message's text. `trust: false` keeps
 * \href, \url and raw HTML out; the size limits keep a hostile formula from
 * eating the page. What it cannot parse comes back as the source, in red.
 */
export function mathHtml (tex, display) {
  const key = (display ? 'D' : 'I') + tex
  let out = rendered.get(key)
  if (out === undefined) {
    try {
      out = katex.renderToString(tex, {
        displayMode: display,
        throwOnError: false,
        trust: false,
        strict: 'ignore',
        maxSize: 20,
        maxExpand: 300,
        output: 'htmlAndMathml'
      })
    } catch {
      out = null
    }
    if (rendered.size > 400) rendered.delete(rendered.keys().next().value)
    rendered.set(key, out)
  }
  return out
}

function Math_ (mark) {
  const markup = mathHtml(mark.value, mark.display)
  if (!markup) return mark.display ? '$$' + mark.value + '$$' : '$' + mark.value + '$'
  return mark.display
    ? html`<span class="math math-block" dangerouslySetInnerHTML=${{ __html: markup }}></span>`
    : html`<span class="math" dangerouslySetInnerHTML=${{ __html: markup }}></span>`
}

function Spoiler ({ text }) {
  return html`<span class="spoiler" onClick=${(e) => e.currentTarget.classList.toggle('revealed')}>${text}</span>`
}

export function normaliseUrl (value) {
  const url = String(value)
  return /^https?:\/\//i.test(url) ? url : 'https://' + url
}

/**
 * Read markdown-ish shorthand out of the composer into stored entities, so
 * `*bold*` round-trips as a real entity rather than literal asterisks.
 */
/**
 * Code markers are matched first and the spans they produce are then off
 * limits, so a `*` inside a code block stays an asterisk instead of opening
 * a bold run.
 */
const CODE_SHORTHAND = [
  { re: /```([\s\S]+?)```/g, type: ENTITY.PRE },
  { re: /`([^`\n]+)`/g, type: ENTITY.CODE }
]

const SHORTHAND = [
  { re: /\*\*([^*\n]+)\*\*/g, type: ENTITY.BOLD },
  { re: /(?<![\w*])\*([^*\n]+)\*(?![\w*])/g, type: ENTITY.BOLD },
  { re: /(?<![\w_])__([^_\n]+)__(?![\w_])/g, type: ENTITY.UNDERLINE },
  { re: /(?<![\w_])_([^_\n]+)_(?![\w_])/g, type: ENTITY.ITALIC },
  { re: /~~([^~\n]+)~~/g, type: ENTITY.STRIKE },
  { re: /\|\|([^|\n]+)\|\|/g, type: ENTITY.SPOILER }
]

export function parseShorthand (input) {
  let text = String(input)
  const entities = []

  // Code first, and its ranges are then off limits: `*` inside a code block
  // is an asterisk, not the start of a bold run.
  const protectedRanges = []

  for (const { re, type } of CODE_SHORTHAND) {
    applyRule(re, type, { protect: true })
  }
  for (const { re, type } of SHORTHAND) {
    applyRule(re, type, { protect: false })
  }

  entities.sort((a, b) => a.offset - b.offset)
  return { text, entities }

  function applyRule (re, type, { protect }) {
    let searchFrom = 0
    let guard = 0

    while (guard++ < 500) {
      re.lastIndex = searchFrom
      const match = re.exec(text)
      if (!match) break

      const offset = match.index
      const inner = match[1]

      // Leave anything inside a code span exactly as it was typed.
      if (isProtected(offset)) {
        searchFrom = offset + match[0].length
        continue
      }

      text = text.slice(0, offset) + inner + text.slice(offset + match[0].length)
      const delta = match[0].length - inner.length

      shift(entities, offset, delta)
      for (const range of protectedRanges) {
        if (range.start > offset) {
          range.start -= delta
          range.end -= delta
        }
      }

      entities.push({ type, offset, length: inner.length, value: '' })
      if (protect) protectedRanges.push({ start: offset, end: offset + inner.length })

      searchFrom = offset + inner.length
    }
  }

  function isProtected (offset) {
    return protectedRanges.some((range) => offset >= range.start && offset < range.end)
  }

  function shift (list, offset, delta) {
    for (const item of list) {
      if (item.offset > offset) item.offset -= delta
    }
  }
}

/* ------------------------------------------------------------------ *
 * Message previews
 * ------------------------------------------------------------------ */

export function messagePreview (message) {
  if (!message) return ''
  if (message.deleted) return t('conv.deleted')

  switch (message.kind) {
    case KIND.IMAGE: return '🖼 ' + (message.text || 'Photo')
    case KIND.VIDEO: return '🎬 ' + (message.text || 'Video')
    case KIND.AUDIO: return '🎤 ' + duration((message.attachments[0] || {}).duration || 0)
    case KIND.FILE: return '📎 ' + ((message.attachments[0] || {}).name || 'File')
    case KIND.GIF: return 'GIF'
    case KIND.STICKER: return message.text || '🙂'
    case KIND.LOCATION: return '📍'
    case KIND.CONTACT: return '👤'
    case KIND.POLL: return '📊 ' + (message.text || '')
    case KIND.SYSTEM: return systemText(message)
    default: return message.text || ''
  }
}

/**
 * A call, in one line: whose it was matters as much as how it went. Both ends
 * used to read "missed call" for a call nobody picked up, and neither could
 * tell whether it was theirs or the other person's.
 */
export function callLine ({ state, duration = 0, out = false } = {}) {
  if (state === CALL_STATE.MISSED) return out ? t('sys.callNoAnswer') : t('sys.callMissed')
  if (state === CALL_STATE.DECLINED) return t('sys.callDeclined')
  return t('sys.callEnded', { duration: durationText(duration) })
}

export function systemText (message, nameOf = (id) => shortId(id), me = '') {
  const extra = message.extra || {}
  switch (extra.event) {
    case 'join': return t('sys.join', { name: extra.name || nameOf(extra.target) })
    case 'leave': return t('sys.leave', { name: extra.name || nameOf(extra.target) })
    case 'kick': return t('sys.kick', { name: extra.name || nameOf(extra.target) })
    case 'role': return t('sys.role', { name: extra.name || nameOf(extra.target), role: roleLabel(extra.role) })
    case 'call':
      return callLine({ state: extra.state, duration: extra.duration || 0, out: !!me && message.author === me })
    default: return message.text || ''
  }
}

/* ------------------------------------------------------------------ *
 * Misc
 * ------------------------------------------------------------------ */

export async function copyText (value) {
  try {
    await navigator.clipboard.writeText(value)
    return true
  } catch {
    // Clipboard API needs a secure context; fall back to the old trick.
    const area = document.createElement('textarea')
    area.value = value
    area.style.position = 'fixed'
    area.style.opacity = '0'
    document.body.appendChild(area)
    area.select()
    let ok = false
    try { ok = document.execCommand('copy') } catch { ok = false }
    document.body.removeChild(area)
    return ok
  }
}

export function blobUrl (bytes, mime) {
  return URL.createObjectURL(new Blob([bytes], { type: mime || 'application/octet-stream' }))
}

/** Downsample PCM into the bar heights a voice note shows. */
export function computeWaveform (samples, buckets = 40) {
  if (!samples || !samples.length) return new Uint8Array(buckets)
  const out = new Uint8Array(buckets)
  const per = Math.floor(samples.length / buckets) || 1
  for (let i = 0; i < buckets; i++) {
    let peak = 0
    for (let j = i * per; j < (i + 1) * per && j < samples.length; j++) {
      const v = Math.abs(samples[j])
      if (v > peak) peak = v
    }
    out[i] = Math.min(255, Math.round(peak * 255))
  }
  return out
}
